// Shared recall flow for receipts that need to be loaded into the cart.
//
// Two entry points share the post-fetch pipeline below:
//
//   * `useRecallReceipt` — held receipts (PosReceiptStatus.Hold *and*
//     on_hold=true). Flips on_hold off via PUT /hold {hold:false}.
//     Used by the "Held Receipts" modal (Functions → Recall Hold) and
//     the Orders tab's "On Hold" list.
//
//   * `useFinishEcomSale` — ecom orders (online=true, on_hold=false). Created
//     at status=Hold; flips Hold→New via PUT /status {status:0} (and the call
//     is idempotent if a prior finish already left it at New). Used by the
//     "Complete" button in OrderDetailModal so the cashier can take payment at
//     the register against the SAME receipt (no duplicate).
//
// Both then run the same five-step pipeline:
//   1. (status flip — done above)
//   2. loadReceiptIntoCart — synthesize cart lines from receipt items.
//   3. setServerTotals — populate CartSummary with server-confirmed totals.
//   4. (binding) loadReceiptIntoCart (step 2) sets currentReceiptId, which
//      buildCommitRequest emits as receipt_id so the next /cart/commit
//      truncate-and-replaces this receipt's items in place rather than
//      creating a sibling.
//   5. Restore refund mode if the receipt is a refund (negative total
//      or is_return / src_id link).

import { useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  useHoldReceipt, useSetReceiptStatus, RECEIPT_STATUS, fetchReceiptDetail,
} from '@/api/receipts'
import type { Receipt as ReceiptType } from '@/api/receipts'
import {
  loadProductsForRecall,
  fetchProductBatches,
  productKeys,
  isSyntheticLotKey,
} from '@/api/products'
import { pickFifoLot } from '@/lib/batches'
import { usePOSConfig } from '@/api/config'
import { usePOSStore } from '@/stores/posStore'

export interface RecallResult {
  receipt: ReceiptType
  isRefund: boolean
}

// `ReturnType<typeof usePOSStore>` resolves to `unknown` because Zustand's
// UseBoundStore is callable with both no-arg and selector overloads;
// TypeScript picks the selector overload's generic `U` (defaulting to
// `unknown`). `getState` is a plain method, so its ReturnType resolves
// cleanly to the full store shape.
type POSStoreState = ReturnType<typeof usePOSStore.getState>
type LoadReceiptIntoCart = POSStoreState['loadReceiptIntoCart']
type SetServerTotals = POSStoreState['setServerTotals']

// Shared post-status-change pipeline. Operates on the receipt the BE
// returned from the status-flip PUT. Pure on resolved store handles --
// hooks pull the handles from React context and pass them in.
async function applyReceiptToCart(
  receipt: ReceiptType,
  receiptId: number,
  queryClient: QueryClient,
  loadReceiptIntoCart: LoadReceiptIntoCart,
  setServerTotals: SetServerTotals,
  scanConfirmEnabled: boolean,
): Promise<RecallResult> {
  const customer = receipt.customer
    ? {
        id: receipt.customer.id,
        display_name: receipt.customer.display_name,
        first_name: receipt.customer.first_name,
        last_name: receipt.customer.last_name,
        is_blocked: receipt.customer.is_blocked,
      }
    : null

  const totalNumber = Number(receipt.totals?.total ?? 0)
  const isRefund =
    receipt.is_return === true ||
    Boolean(receipt.src_id) ||
    Boolean(receipt.return_receipt_id) ||
    (Number.isFinite(totalNumber) && totalNumber < 0)

  // Pull rich catalog data for each receipt line so the recalled cart
  // rows render with images, brand, strain, THC%, IOH — same as a
  // freshly-added row. Cache hits are free; misses fan out parallel
  // /objects/products/{id} fetches.
  const items = receipt.items ?? []
  const uniqueIds = Array.from(
    new Set(
      items
        .map((i) => i.product_id)
        .filter((id): id is number => typeof id === 'number' && id > 0),
    ),
  )
  const productMap = await loadProductsForRecall(queryClient, uniqueIds)

  // FIFO lot resolution. Ecom/hold receipts arrive with no lot on their
  // cannabis lines, so the LOT badge stays blank until the cashier opens
  // the product modal (whose pickFifoLot assigns one). Resolve it here so
  // the badge shows on load — same FIFO pick, same batches cache key the
  // modal/optimistic-add use, so the later modal open is a cache hit.
  // Skipped for refunds (returning product, not picking new stock).
  const lotMap = new Map<number, string>()
  if (!isRefund) {
    const customerId = receipt.customer?.id ?? null
    const needsLot = uniqueIds.filter(
      (id) =>
        productMap.get(id)?.is_cannabis === true &&
        items.some((i) => i.product_id === id && !i.lot_number),
    )
    await Promise.all(
      needsLot.map(async (id) => {
        try {
          const batches = await queryClient.fetchQuery({
            queryKey: [...productKeys.detail(id), 'batches', customerId],
            queryFn: () => fetchProductBatches(id, customerId),
            staleTime: 60 * 1000,
          })
          const lot = pickFifoLot(batches)
          if (lot && !isSyntheticLotKey(lot)) lotMap.set(id, lot)
        } catch (err) {
          console.error('[recall] FIFO batch resolution failed', { product_id: id, err })
        }
      }),
    )
  }

  loadReceiptIntoCart(
    receiptId,
    items,
    customer as Parameters<typeof loadReceiptIntoCart>[2],
    {
      productMap,
      lotMap,
      // Arm scan-to-confirm only for non-refund hold/ecom recalls.
      scanConfirm: scanConfirmEnabled && !isRefund,
      ...(isRefund
        ? {
            isRefund: true,
            refundSourceReceipt: {
              id: receipt.src_id ?? receipt.return_receipt_id ?? receipt.id,
              number: receipt.src_number ?? receipt.return_number ?? receipt.number,
            },
          }
        : {}),
    },
  )

  if (receipt.totals) {
    const t = receipt.totals
    setServerTotals({
      subtotal: Number(t.subtotal ?? 0),
      taxes: Number(t.taxes ?? 0),
      deposit: Number((t as unknown as Record<string, unknown>).deposit_amount ?? 0),
      total: Number(t.total ?? 0),
      amountToPay: Number(receipt.amount_to_pay ?? t.total ?? 0),
    })
  }

  return { receipt, isRefund }
}

// PUT /hold can return either a bare receipt or a {receipt} envelope
// depending on which BE handler version answers. Normalize.
function unwrapReceipt(response: unknown): ReceiptType {
  if (response && typeof response === 'object' && 'receipt' in response) {
    return (response as { receipt: ReceiptType }).receipt
  }
  return response as ReceiptType
}

export function useRecallReceipt() {
  const { loadReceiptIntoCart, setServerTotals } = usePOSStore()
  const { data: posConfig } = usePOSConfig()
  const unholdReceipt = useHoldReceipt()
  const queryClient = useQueryClient()

  const recall = async (receiptId: number): Promise<RecallResult> => {
    const response = await unholdReceipt.mutateAsync({ receiptId, onHold: false })
    const receipt = unwrapReceipt(response)
    return applyReceiptToCart(
      receipt, receiptId, queryClient, loadReceiptIntoCart, setServerTotals,
      posConfig?.posOnHoldScanToConfirmLot === true,
    )
  }

  return { recall, isPending: unholdReceipt.isPending }
}

export function useFinishEcomSale() {
  const { loadReceiptIntoCart, setServerTotals, setLoadedEcomMeta } = usePOSStore()
  const { data: posConfig } = usePOSConfig()
  const setStatus = useSetReceiptStatus()
  const queryClient = useQueryClient()

  // Ecom orders are created at status=Hold with on_hold=false. Flipping to
  // New (idempotent if a prior finish already left it at New) makes the
  // receipt the cart's active working set; payment then drives it through the
  // normal Paid path. applyReceiptToCart binds currentReceiptId so the
  // pay-time /cart/commit overwrites THIS receipt in place rather than
  // minting a duplicate. If the BE rejects the transition (e.g. a LOCKED
  // guard), the promise rejects and the modal surfaces the error toast.
  //
  // `priorStatus` is the receipt's status BEFORE this flip (normally Hold).
  // We stash it in loadedEcomMeta so the POS "Close" action can revert the
  // order to exactly where it was -- the clerk may just be viewing it with
  // no customer present, and closing must NOT cancel the order.
  const finish = async (
    receiptId: number,
    priorStatus: number = RECEIPT_STATUS.HOLD,
  ): Promise<RecallResult> => {
    const response = await setStatus.mutateAsync({
      receiptId, status: RECEIPT_STATUS.NEW,
    })
    const receipt = unwrapReceipt(response)
    const result = await applyReceiptToCart(
      receipt, receiptId, queryClient, loadReceiptIntoCart, setServerTotals,
      posConfig?.posOnHoldScanToConfirmLot === true,
    )
    // Set AFTER applyReceiptToCart -- loadReceiptIntoCart resets the meta to
    // null, so stamping it here is what marks this load as a closeable ecom.
    setLoadedEcomMeta({
      isDelivery: receipt.is_delivery === true,
      priorStatus,
    })
    return result
  }

  return { finish, isPending: setStatus.isPending }
}

// Read-only load of an online-PAID (PaidOnline=32) order so the cashier can
// verify the items at the register before handing them over. Unlike
// useFinishEcomSale there is NO status flip -- PaidOnline is LOCKED
// server-side (the charge must match the cart exactly) and stays that way.
// The paidOnline meta flag makes the cart a viewing surface: adds/edits are
// gated (posStore.addToCart, CartItemRow), Checkout renders as "Complete"
// and runs /complete-pickup, and Close simply clears the local cart.
export function useLoadPaidOnlineOrder() {
  const { loadReceiptIntoCart, setServerTotals, setLoadedEcomMeta } = usePOSStore()
  const { data: posConfig } = usePOSConfig()
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = useState(false)

  const load = async (receiptId: number): Promise<RecallResult> => {
    setIsPending(true)
    try {
      const receipt = await fetchReceiptDetail(receiptId, { includeItems: true })
      const result = await applyReceiptToCart(
        receipt, receiptId, queryClient, loadReceiptIntoCart, setServerTotals,
        posConfig?.posOnHoldScanToConfirmLot === true,
      )
      // Set AFTER applyReceiptToCart (loadReceiptIntoCart resets the meta).
      setLoadedEcomMeta({
        isDelivery: receipt.is_delivery === true,
        priorStatus: RECEIPT_STATUS.PAID_ONLINE,
        paidOnline: true,
      })
      return result
    } finally {
      setIsPending(false)
    }
  }

  return { load, isPending }
}

// Close a loaded ecom/delivery order WITHOUT cancelling it. Reverts the
// receipt to the status it had before it was pulled into the register
// (loadedEcomMeta.priorStatus -- normally Hold) and clears the cart, so the
// order returns to the Deliveries/Ecom list unchanged. The real "cancel an
// ecom order" path is the OrderDetailModal "Cancel Order" button
// (/cancel-order, which emails the customer) -- unaffected.
export function useCloseEcomReceipt() {
  const { loadedEcomMeta, currentReceiptId, clearReceipt } = usePOSStore()
  const setStatus = useSetReceiptStatus()

  const close = async (): Promise<void> => {
    const id = currentReceiptId
    const priorStatus = loadedEcomMeta?.priorStatus
    // Paid-online view loads never flipped the status (the receipt is
    // LOCKED at PaidOnline throughout), so there is nothing to revert on
    // the BE -- and a PUT /status against a LOCKED receipt would 400.
    if (loadedEcomMeta?.paidOnline) {
      clearReceipt()
      return
    }
    // Revert on the BE first; only clear locally once it's parked, so a
    // failed revert leaves the cashier on the still-loaded order to retry.
    if (id != null && priorStatus != null) {
      await setStatus.mutateAsync({ receiptId: id, status: priorStatus })
    }
    clearReceipt()
  }

  return { close, isPending: setStatus.isPending }
}
